import {
    StyledDecisionSupport,
    StyledEdit,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { DecisionSupportDialogShell } from '@/components/decisionSupport/DecisionSupportDialogShell';
import { DecisionSupportSelectionContent } from '@/components/decisionSupport/DecisionSupportSelectionContent';
import { VisitAssignmentDateRecommendationsQuery } from '@/components/decisionSupport/queries/visitAssignment.gql.types';
import { DecisionSupportSelection } from '@/components/decisionSupport/utils/decisionSupport.utils';
import { normalizeVisitReassignmentDate } from '@/components/decisionSupport/utils/visitReassignmentRecommendations.utils';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { IconButton } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

type DateOutput = NonNullable<
    VisitAssignmentDateRecommendationsQuery['decisionSupport']['visitAssignment']['dateRecommendations']
>;
type AssignmentDefinitions = DateOutput['definitions'];
type AssignmentDateRecommendation = DateOutput['results'][number]['dateRecommendation'];

// Selection id is the ISO `YYYY-MM-DD` date (matching the normalizer's row id);
// label is the display-formatted date.
type AssignmentDateDecisionSupportContentProps = {
    onClose(): void;
    definitions: AssignmentDefinitions | null;
    dateRecommendation: AssignmentDateRecommendation | null;
    selectedDate: DecisionSupportSelection | null;
    setSelectedDate(selection: DecisionSupportSelection | null): void;
};

const AssignmentDateDecisionSupportContent: FC<AssignmentDateDecisionSupportContentProps> = ({
    onClose,
    definitions,
    dateRecommendation,
    selectedDate,
    setSelectedDate,
}) => {
    const normalized = useMemo(() => {
        const result = normalizeVisitReassignmentDate(dateRecommendation, definitions);
        // BE-577 stopgap: the engine is meant to return dates chronologically but
        // regressed to rank order. Sort both buckets by date here until the backend
        // fix lands. Row ids are ISO `YYYY-MM-DD`, so a string compare is chronological.
        const byDate = (a: { id: string }, b: { id: string }) => a.id.localeCompare(b.id);
        return {
            ...result,
            recommended: [...result.recommended].sort(byDate),
            notRecommended: [...result.notRecommended].sort(byDate),
        };
    }, [dateRecommendation, definitions]);

    return (
        <DecisionSupportSelectionContent
            onClose={onClose}
            normalized={normalized}
            selection={selectedDate}
            onConfirm={setSelectedDate}
            candidateHeader='Date'
            itemNoun='dates'
            showRecommendationSearch={false}
        />
    );
};

export type AssignmentDateDecisionSupportDialogProps = {
    onClose(): void;
    definitions: AssignmentDefinitions | null;
    dateRecommendation: AssignmentDateRecommendation | null;
    selectedDate: DecisionSupportSelection | null;
    setSelectedDate(selection: DecisionSupportSelection | null): void;
};

export const AssignmentDateDecisionSupportDialog: FC<AssignmentDateDecisionSupportDialogProps> = (
    props,
) => {
    const { onClose, ...rest } = props;

    return (
        <DecisionSupportDialogShell title='Date Recommendations' onClose={onClose}>
            {({ onClose }) => <AssignmentDateDecisionSupportContent onClose={onClose} {...rest} />}
        </DecisionSupportDialogShell>
    );
};

type AssignmentDateDecisionSupportProps = {
    definitions: AssignmentDefinitions | null;
    dateRecommendation: AssignmentDateRecommendation | null;
    selectedDate: DecisionSupportSelection | null;
    setSelectedDate(selection: DecisionSupportSelection | null): void;
    disabled?: boolean;
};

// Edit-icon trigger that opens the new visitAssignment date dialog.
export const AssignmentDateDecisionSupport: FC<AssignmentDateDecisionSupportProps> = (props) => {
    const { definitions, dateRecommendation, selectedDate, setSelectedDate, disabled } = props;
    const { openDialog } = useDialog();

    const onClickEdit = useCallback(() => {
        openDialog(DialogType.AssignmentDateDecisionSupport, {
            definitions,
            dateRecommendation,
            selectedDate,
            setSelectedDate,
        });
    }, [openDialog, definitions, dateRecommendation, selectedDate, setSelectedDate]);

    const hasRows =
        (dateRecommendation?.recommendations.length ?? 0) > 0 ||
        (dateRecommendation?.incompatibleDates.length ?? 0) > 0;
    if (!hasRows) {
        return null;
    }

    return (
        <StyledDecisionSupport>
            <IconButton onClick={onClickEdit} disabled={disabled} aria-label='Edit date selection'>
                <StyledEdit />
            </IconButton>
        </StyledDecisionSupport>
    );
};
